import { type Config } from 'tailwindcss'
import defaultTheme from 'tailwindcss/defaultTheme.js'

const base = {
  fontSize: {
    /** 44px size / 52px high / normal */
    '8xl': ['2.75rem', { lineHeight: '3.25rem', letterSpacing: '0.002em' }],
    /** 36px size / 44px high / normal */
    '7xl': ['2.25rem', { lineHeight: '2.75rem', letterSpacing: '0.002em' }],
    /** 32px size / 36px high / normal */
    '6xl': ['2rem', { lineHeight: '2.25rem', letterSpacing: '0.002em' }],
    /** 28px size / 36px high / normal */
    '5xl': ['1.75rem', { lineHeight: '2.25rem', letterSpacing: '0.002em' }],
    /** 26px size / 30px high / normal */
    '4xl': ['1.625rem', { lineHeight: '1.875rem', letterSpacing: '0.002em' }],
    /** 24px size / 32px high / normal */
    '3xl': ['1.5rem', { lineHeight: '2rem', letterSpacing: '0.002em' }],
    /** 22px size / 30px high / normal */
    '2xl': ['1.375rem', { lineHeight: '1.875rem', letterSpacing: '0.002em' }],
    /** 20px size / 28px high / normal */
    xl: ['1.25rem', { lineHeight: '1.75rem', letterSpacing: '0.002em' }],
    /** 18px size / 26px high / normal */
    lg: ['1.125rem', { lineHeight: '1.625rem', letterSpacing: '0.002em' }],
    /** 16px size / 24px high / normal */
    base: ['1rem', { lineHeight: '1.5rem', letterSpacing: '0.002em' }],
    /** 14px size / 20px high / normal */
    sm: ['0.875rem', { lineHeight: '1.25rem', letterSpacing: '0.002em' }],
    /** 12px size / 20px high / normal */
    xs: ['0.75rem', { lineHeight: '1.25rem', letterSpacing: '0.002em' }],
  },
} satisfies Config['theme']

export const theme = {
  colors: {
    'bodi-black': '#101010',
    'bodi-black-text': '#161819',
    'bodi-white': '#FFFFFF',
    'bodi-gray': '#F5F5F5',
    'bodi-gray-butters': '#BDBDBD',
    'bodi-gray-dark': '#444646',
    'bodi-gray-medium': '#706F73',
    'bodi-gray-disabled': '#A09EA3',
    'bodi-gray-light': '#E8EAEE',
    'bodi-gray-pearl': '#F7F8FA',
    'bodi-cool-gray': {
      200: '#d8dbdf',
      300: '#b6bac3',
      400: '#8e95a2',
      500: '#6b7280',
      600: '#5b616e',
      700: '#4a4e5a',
    },
    'bodi-green-deep': {
      300: '#A6CEC3',
      400: '#63A995',
      500: '#208468',
      600: '#0D6B51',
      700: '#01533D',
    },
    'bodi-green-bright': {
      300: '#E5FDCF',
      400: '#D1FBAB',
      500: '#BCF986',
      600: '#99F646',
      700: '#8DE341',
    },
    'bodi-green-success': '#198754',
    'bodi-yellow-deep': {
      300: '#FFEBA8',
      400: '#FFDB67',
      500: '#FFCC26',
      600: '#FFC200',
      700: '#F5B800',
    },
    'bodi-yellow-light': {
      300: '#FFF9DA',
      400: '#FFF5BF',
      500: '#FFF0A3',
      600: '#FFE560',
      700: '#F5D530',
    },
    'bodi-yellow-warning': '#FDD03B',
    'bodi-blue-deep': {
      300: '#BEEBEB',
      400: '#8DD9DB',
      500: '#5CCCCC',
      600: '#36BCBC',
      700: '#079D9D',
    },
    'bodi-blue-light': {
      300: '#EDFAFF',
      400: '#DCF2FA',
      500: '#CDECF6',
      600: '#ADDEEE',
      700: '#77C8E2',
    },
    'bodi-blue-info': '#0FB1D2',
    'bodi-red-deep': {
      300: '#E5B4B3',
      400: '#D27B79',
      500: '#BE4340',
      600: '#981612',
      700: '#7E0400',
    },
    'bodi-red-light': {
      300: '#FAE8DC',
      400: '#F7D6C2',
      500: '#F3C5A8',
      600: '#F3A473',
      700: '#ED8747',
    },
    'bodi-red-error': '#D50000',
    'bodi-input-selected': '#1ACD7B',
    'bodi-input-active-label': '#825BB5',
    'bodi-input-disabled-label': '#BAA3D9',
  },
  fontSize: {
    // Default tailwind size names
    ...base.fontSize,

    // Aliases
    'body-xl': base.fontSize.xl,
    'body-lg': base.fontSize.lg,
    'body-md': base.fontSize.base, // TODO: remove this once all uses are gone
    'body-base': base.fontSize.base,
    'body-sm': base.fontSize.sm,
    'body-xs': base.fontSize.xs,
  },
  fontFamily: {
    sans: ['var(--font-heebo)', ...defaultTheme.fontFamily.sans],
    lateral: ['var(--font-lateral)', ...defaultTheme.fontFamily.sans],
    arial: ['Arial', ...defaultTheme.fontFamily.sans],
  },
  keyframes: {
    scrollLeft: {
      '0%': {
        transform: 'translate3d(0, 0, 0)',
      },
      '100%': {
        transform: 'translate3d(-33.333%, 0, 0)',
      },
    },
    scrollRight: {
      '100%': {
        transform: 'translate3d(0, 0, 0)',
      },
      '0%': {
        transform: 'translate3d(-33.333%, 0, 0)',
      },
    },
  },
  animation: {
    scrollLeft: 'scrollLeft 300s linear infinite',
    scrollRight: 'scrollRight 300s linear infinite',
  },
  zIndex: {
    max: '99999999',
  },
} satisfies Config['theme']
